/* SVADEZI — PDP option treatments matched to the brand app.

   The app shows three DISTINCT option designs; the Shopify port rendered all
   three as identical flat pills. Rather than fork the Liquid, this enhances
   the existing markup in place, so it stays driven by REAL Shopify variants
   and degrades to plain pills if the option names ever change.

     Purity      -> big serif karat + small caps metal, gold fill when active
     Metal Color -> round colour swatch + word, dark fill when active
     Stone Type  -> 2-up cards, title + sub, gold-pale wash when active

   COMPOUND METAL OPTION
   ---------------------
   The catalogue has two option layouts:
     Metal - Purity | Color | Stone Type      (32 of 48 sampled)
     Metal - Finish | Stone Type | Ring Size  (16 of 48)
   The second fuses purity and colour into ONE option: "14 KT Rose Gold",
   "925 Silver Yellow Plated". classify() saw "finish" in the name and rendered
   colour-only pills, which DISCARDED the purity — four pills reading "Rhodium"
   and four reading "Yellow", with nothing to separate 9KT from 14KT from 925.

   splitCompound() renders two selectors over that one option so those products
   read like the rest of the catalogue. The real radios stay in the DOM as the
   single source of truth and are merely hidden; the synthesised pills tick a
   real radio and dispatch a bubbling `change`, so pdp-variant-sync.js and
   pdp-breakdown.js keep working untouched. The synthesised rows are
   `.opt-synth`, deliberately NOT `.opt`, because both readers map group index
   -> option index and a fourth `.opt` would shift that mapping and mis-price.

   The colour list is derived per product, so a gents ring — which carries only
   Rhodium and Yellow, no Rose — renders two colour pills, not three.

   REACHABILITY: a value can be a legitimate option value and still have no
   variant for the currently chosen stone and size. Availability is therefore
   judged against window.SvadeziVariants (the full 408-row matrix), not the
   option value list — otherwise a pill would highlight while the price and the
   cart stayed on the previous variant. */
(function () {
  var FINISH = {
    yellow:  { label: 'Yellow',  dot: '#cba25a' },
    rose:    { label: 'Rose',    dot: '#c98a7a' },
    rhodium: { label: 'Rhodium', dot: '#dcdcdc' },
    white:   { label: 'White',   dot: '#dcdcdc' }
  };
  var COLOUR_ORDER = ['yellow', 'rose', 'rhodium', 'white'];
  var STONE_SUB = { lab: 'VVS/EF · IGI Certified', natural: 'VVS/EF · Mined Diamond' };

  function karat(v) {                 // "Gold 18K" -> 18K ; "Sterling Silver 925" -> 925
    var m = String(v).match(/(\d+K|925)/i);
    return m ? m[1].toUpperCase() : null;
  }
  function finishKey(v) {
    var t = String(v).toLowerCase();
    return Object.keys(FINISH).find(function (k) { return t.indexOf(k) !== -1; }) || null;
  }
  function stoneKey(v) {
    var t = String(v).toLowerCase();
    if (t.indexOf('lab') !== -1) return 'lab';
    if (t.indexOf('natural') !== -1 || t.indexOf('mined') !== -1) return 'natural';
    return null;
  }

  /* "14 KT Rose Gold" -> {purity:'14K', colour:'rose'}
     "925 Silver Yellow Plated" -> {purity:'925', colour:'yellow'}
     Note the space in "14 KT", which karat() above does NOT match. Returns null
     unless BOTH halves parse, so an unrecognised value aborts the whole split
     rather than guessing at a metal. */
  function parseCompound(raw) {
    var s = String(raw);
    var km = s.match(/(\d+)\s*K(?:T)?\b/i);
    var purity = km ? km[1] + 'K' : (/\b925\b/.test(s) ? '925' : null);
    var colour = null;
    if (/rhodium/i.test(s)) colour = 'rhodium';
    else if (/rose/i.test(s)) colour = 'rose';
    else if (/yellow/i.test(s)) colour = 'yellow';
    else if (/white/i.test(s)) colour = 'white';
    return (purity && colour) ? { purity: purity, colour: colour, raw: raw } : null;
  }
  function purityRank(p) { return p === '925' ? 999 : (parseInt(p, 10) || 0); }

  function makeSynthGroup(kind, heading) {
    var host = document.createElement('div');
    host.className = 'opt-synth';
    host.setAttribute('data-opt-kind', kind);
    var lab = document.createElement('div');
    lab.className = 'opt-label';
    lab.textContent = heading;
    var row = document.createElement('div');
    row.className = 'opt-row';
    host.appendChild(lab);
    host.appendChild(row);
    return { host: host, row: row };
  }

  function splitCompound(group, labelEl, row) {
    var radios = Array.prototype.slice.call(row.querySelectorAll('input[type=radio]'));
    if (radios.length < 4) return false;

    var parsed = radios.map(function (r) {
      var p = parseCompound(r.value);
      if (p) p.input = r;
      return p;
    });
    if (parsed.some(function (p) { return !p; })) return false;   // don't guess

    var purities = [], colours = [];
    parsed.forEach(function (p) {
      if (purities.indexOf(p.purity) === -1) purities.push(p.purity);
      if (colours.indexOf(p.colour) === -1) colours.push(p.colour);
    });
    // only a genuine 2-D grid can honestly be shown as two selectors
    if (purities.length < 2 || colours.length < 2) return false;

    purities.sort(function (a, b) { return purityRank(a) - purityRank(b); });
    colours.sort(function (a, b) { return COLOUR_ORDER.indexOf(a) - COLOUR_ORDER.indexOf(b); });

    group.setAttribute('data-opt-kind', 'compound');
    labelEl.textContent = 'Metal';

    function find(purity, colour) {
      return parsed.filter(function (p) {
        return p.purity === purity && p.colour === colour;
      })[0] || null;
    }
    function current() {
      return parsed.filter(function (p) { return p.input.checked; })[0] || parsed[0];
    }
    /* Would picking `raw` resolve to a real variant, given the OTHER options as
       they stand right now? Returns true when the matrix is unavailable, so a
       missing metafield degrades to "show everything" rather than hiding stock. */
    function reachable(raw) {
      var M = window.SvadeziVariants;
      if (!M || !M.available()) return true;
      var vals = Array.prototype.map.call(document.querySelectorAll('.opt'), function (g) {
        if (g === group) return raw;
        var c = g.querySelector('input[type=radio]:checked, select');
        return c ? c.value : null;
      });
      if (vals.some(function (v) { return v === null; })) return true;
      return M.exists(vals);
    }
    function choose(entry) {
      if (!entry) return;
      entry.input.checked = true;
      // bubbles so pdp-variant-sync.js and pdp-breakdown.js re-match and re-price
      entry.input.dispatchEvent(new Event('change', { bubbles: true }));
      render();
    }

    var pg = makeSynthGroup('purity', 'Purity');
    var cg = makeSynthGroup('finish', 'Metal Color');
    pg.row.style.gridTemplateColumns = 'repeat(' + purities.length + ',minmax(0,1fr))';
    cg.row.style.gridTemplateColumns = 'repeat(' + colours.length + ',minmax(0,1fr))';

    purities.forEach(function (p) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'opt-pill';
      b.setAttribute('data-purity', p);
      b.innerHTML = '<b>' + p + '</b><i>' + (p === '925' ? 'Silver' : 'Gold') + '</i>';
      b.addEventListener('click', function () {
        if (b.disabled) return;
        var cur = current();
        var keep = find(p, cur.colour);
        if (keep && reachable(keep.raw)) { choose(keep); return; }
        // this purity does not offer the current colour — take its first reachable one
        choose(colours.map(function (c) { return find(p, c); })
                      .filter(function (e) { return e && reachable(e.raw); })[0]);
      });
      pg.row.appendChild(b);
    });

    colours.forEach(function (c) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'opt-pill';
      b.setAttribute('data-colour', c);
      b.innerHTML = '<span class="opt-dot" style="background:' + FINISH[c].dot +
                    '"></span><span>' + FINISH[c].label + '</span>';
      b.addEventListener('click', function () {
        if (b.disabled) return;
        choose(find(current().purity, c));
      });
      cg.row.appendChild(b);
    });

    function render() {
      var cur = current();
      Array.prototype.forEach.call(pg.row.children, function (b) {
        var p = b.getAttribute('data-purity');
        var ok = colours.some(function (c) {
          var e = find(p, c);
          return e && reachable(e.raw);
        });
        b.setAttribute('aria-pressed', p === cur.purity ? 'true' : 'false');
        b.disabled = !ok;
        b.classList.toggle('is-unavailable', !ok);
      });
      Array.prototype.forEach.call(cg.row.children, function (b) {
        var c = b.getAttribute('data-colour');
        var e = find(cur.purity, c);
        var ok = !!e && reachable(e.raw);
        b.setAttribute('aria-pressed', c === cur.colour ? 'true' : 'false');
        b.disabled = !ok;
        b.classList.toggle('is-unavailable', !ok);
      });
    }

    group.parentNode.insertBefore(pg.host, group.nextSibling);
    pg.host.parentNode.insertBefore(cg.host, pg.host.nextSibling);

    /* Re-render on ANY option change: switching stone or size changes which
       metals are reachable, and the real radios can also be flipped elsewhere. */
    var scope = group.closest('form') || document;
    scope.addEventListener('change', function () { window.setTimeout(render, 0); });
    render();
    return true;
  }

  function classify(labelText) {
    var t = String(labelText).toLowerCase();
    if (/colour|color|finish/.test(t)) return 'finish';
    if (/stone|diamond/.test(t)) return 'stone';
    if (/purity|karat|carat\s*metal|metal/.test(t)) return 'purity';
    return null;
  }
  var HEADING = { purity: 'Purity', finish: 'Metal Color', stone: 'Diamond Type' };

  function enhance() {
    document.querySelectorAll('.opt').forEach(function (group) {
      if (group.getAttribute('data-opt-kind')) return;    // already done
      var labelEl = group.querySelector('.opt-label');
      var row = group.querySelector('.opt-row');
      if (!labelEl || !row) return;

      /* Tried BEFORE classify(): a fused purity+colour option is named
         "Metal - Finish", which classify() reads as colour-only and would
         render with the purity thrown away. */
      if (splitCompound(group, labelEl, row)) return;

      var kind = classify(labelEl.textContent);
      if (!kind) return;
      group.setAttribute('data-opt-kind', kind);
      labelEl.textContent = HEADING[kind];

      row.querySelectorAll('.opt-pill').forEach(function (pill) {
        var raw = (pill.textContent || '').trim();
        if (kind === 'purity') {
          var k = karat(raw);
          if (!k) return;
          var suffix = /925|silver/i.test(raw) ? 'Silver' : 'Gold';
          pill.innerHTML = '<b>' + k + '</b><i>' + suffix + '</i>';
        } else if (kind === 'finish') {
          var fk = finishKey(raw);
          if (!fk) return;
          pill.innerHTML = '<span class="opt-dot" style="background:' + FINISH[fk].dot +
                           '"></span><span>' + FINISH[fk].label + '</span>';
        } else if (kind === 'stone') {
          var sk = stoneKey(raw);
          if (!sk) return;
          var title = sk === 'lab' ? 'Lab Grown' : 'Natural';
          pill.innerHTML = '<b>' + title + '</b><i>' + STONE_SUB[sk] + '</i>';
        }
      });
    });
  }

  /* sticky bar meta: purity · finish, from whatever is currently selected */
  function stickyMeta() {
    var out = document.querySelector('[data-sticky-meta], .pdp-sticky-price .s');
    if (!out) return;
    var bits = [];
    document.querySelectorAll('.opt').forEach(function (g) {
      var kind = g.getAttribute('data-opt-kind');
      var checked = g.querySelector('input:checked');
      if (!checked || !kind) return;
      if (kind === 'compound') {
        // one option carries both halves
        var pc = parseCompound(checked.value);
        if (pc) { bits.push(pc.purity); bits.push(FINISH[pc.colour].label); }
      }
      if (kind === 'purity') { var k = karat(checked.value); if (k) bits.push(k); }
      if (kind === 'finish') { var f = finishKey(checked.value); if (f) bits.push(FINISH[f].label); }
    });
    if (bits.length) out.textContent = bits.join(' · ').toUpperCase();
  }

  function boot() {
    enhance();
    stickyMeta();
    document.addEventListener('change', function (e) {
      if (e.target.matches('.opt-row input, .opt-row select')) window.setTimeout(stickyMeta, 0);
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else { boot(); }
})();
